// Panel family: the framed surface every screen is built from (11 §10.10). `ornate` adds the gold
// double hairline and corner brackets for featured panels (title, modals, results); `inset` is a
// darker well for lists inside a panel; `plain` is layout-only.
import type { HTMLAttributes, ReactNode } from 'react';

export type PanelVariant = 'default' | 'ornate' | 'plain' | 'inset' | 'solid';
export type PanelTone = 'default' | 'danger' | 'success';

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  /** aria-label when the title is not plain text */
  label?: string;
  /** Right-aligned controls in the title row. */
  actions?: ReactNode;
  /** Footer row under a hairline. */
  foot?: ReactNode;
  variant?: PanelVariant;
  tone?: PanelTone;
  /** Entrance animation on mount. */
  rise?: boolean;
  /** Slow gold glow — for the one thing the player should look at. */
  glow?: boolean;
  /** Skip the `fk-hit` class (board-overlay panels that must let pointer events through). */
  passthrough?: boolean;
  as?: 'section' | 'aside' | 'article' | 'div' | 'header' | 'main';
  children?: ReactNode;
}

export function Panel({ title, label, actions, foot, variant = 'default', tone = 'default', rise, glow, passthrough, as: Tag = 'section', className = '', children, ...rest }: PanelProps) {
  const cls = ['fk-panel', passthrough ? '' : 'fk-hit', variant !== 'default' ? `fk-panel--${variant}` : '', tone !== 'default' ? `fk-panel--tone-${tone}` : '', rise ? 'fk-panel--rise' : '', glow ? 'fk-panel--glow' : '', !children && (title || actions) ? 'fk-panel--head-only' : '', className].filter(Boolean).join(' ');
  const ariaLabel = label ?? (typeof title === 'string' ? title : undefined);
  return (
    <Tag className={cls} aria-label={ariaLabel} {...rest}>
      {(title || actions) && (
        <div className="fk-panel__head">
          {title && <h2 className="fk-panel__title"><span className="fk-panel__title-glyph" aria-hidden="true">◆</span><span className="fk-panel__title-text">{title}</span></h2>}
          {actions && <div className="fk-panel__actions">{actions}</div>}
        </div>
      )}
      {children}
      {foot && <div className="fk-panel__foot">{foot}</div>}
    </Tag>
  );
}

/** Ornamental rule with an optional small-caps label. */
export function Divider({ children, tight, className = '' }: { children?: ReactNode; tight?: boolean; className?: string }) {
  return <div className={`fk-divider ${tight ? 'fk-divider--tight' : ''} ${className}`} role="separator">{children}</div>;
}

export type DisplaySize = 'xl' | 'lg' | 'md' | 'sm';

/** Display heading in the game's serif. `gold` paints it with the gradient. */
export function Display({ size = 'md', gold, as, className = '', children, ...rest }: { size?: DisplaySize; gold?: boolean; as?: 'h1' | 'h2' | 'h3' | 'div' | 'span'; className?: string; children: ReactNode } & HTMLAttributes<HTMLElement>) {
  const Tag = as ?? (size === 'xl' ? 'h1' : size === 'lg' ? 'h2' : 'h3');
  return <Tag className={`fk-display fk-display--${size} ${gold ? 'fk-display--gold' : ''} ${className}`} {...rest}>{children}</Tag>;
}

/** Small-caps gold label above a heading or a group. */
export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`fk-eyebrow ${className}`}>{children}</div>;
}

/** The storyteller's sentence: an italic quote with a gold rail. */
export function Quote({ children, className = '', ...rest }: { children: ReactNode; className?: string } & HTMLAttributes<HTMLQuoteElement>) {
  return <blockquote className={`fk-quote ${className}`} {...rest}>{children}</blockquote>;
}
